import { useState } from "react";
import {
  Search,
  SendHorizontal,
  FileSignature,
  Calendar,
  User,
  X,
} from "lucide-react";
import { getAllDocument } from "../../../DocuSeal/API/DocuSealServices";
import Loader from "../../../DocuSeal/Components/Loader";

const LIMIT = 10;

export default function DocumentSearchPage() {
  const [usernameInput, setUsernameInput] = useState("");
  const [referenceInput, setReferenceInput] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);


  const runSearch = async (targetPage = 1) => {
    if (!usernameInput.trim()) {
      setError("Please enter a username");
      return;
    }
    if (!referenceInput.trim()) {
      setError("Please enter a reference number");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setHasSearched(true);
      setPage(targetPage);

      const res = await getAllDocument({
        page: targetPage,
        limit: LIMIT,
        username: usernameInput.trim().toLowerCase(),
        search: referenceInput.trim(),
        status: "pending",
      });

      // Defensive client-side filter in case the server ignores the status filter
      const pending = (res.submissions || []).filter(
        (s) => (s.status || "").toLowerCase() !== "completed",
      );

      setItems(pending);
      setTotalPages(res.pagination?.total_pages || 1);
      setTotalCount(res.pagination?.total_count ?? pending.length);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to search documents");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (item) => {
    const url =
      item.status?.toLowerCase() === "completed"
        ? item.documents?.[0]?.url
        : item.embed_src || item.documents?.[0]?.url;
    if (!url) {
      setError("Document link not available");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="min-h-screen bg-slate-50"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
    <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-600 via-indigo-600 to-violet-600 p-6 shadow-lg md:p-8">
        <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <h1 className="text-2xl font-bold text-white md:text-3xl">
            Document Search
          </h1>
          <p className="mt-2 max-w-2xl text-base text-white/85 md:text-base">
            Search submissions by username and reference number. Tap any result
            to open it in this page.
          </p>
        </div>
      </div>

      {/* Search form */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
          <div className="relative">
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Username <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value.toLowerCase())}
                onKeyDown={(e) => e.key === "Enter" && runSearch(1)}
                placeholder="e.g. john.doe"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                className="w-full rounded-xl border border-slate-300 py-3.5 pl-11 pr-10 text-base font-medium outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              />
              {usernameInput && (
                <button
                  type="button"
                  onClick={() => setUsernameInput("")}
                  className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 active:bg-slate-100"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          <div className="relative">
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">
              Reference Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch(1)}
                placeholder="Required"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                className="w-full rounded-xl border border-slate-300 py-3.5 pl-11 pr-10 text-base font-medium outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              />
              {referenceInput && (
                <button
                  type="button"
                  onClick={() => setReferenceInput("")}
                  className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 active:bg-slate-100"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={() => runSearch(1)}
          disabled={
            loading || !usernameInput.trim() || !referenceInput.trim()
          }
          className="mt-5 inline-flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-base font-semibold text-white shadow-sm transition active:scale-[0.99] active:from-sky-600 active:to-blue-700 disabled:opacity-50"
        >
          <SendHorizontal className="h-5 w-5" />
          Search
        </button>

        {error && (
          <p className="mt-3 text-sm font-medium text-red-600">{error}</p>
        )}
      </div>

      {/* Results */}
      <div className="mt-6 space-y-3">
        {loading ? (
          <Loader show label="Searching..." />
        ) : !hasSearched ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
            <Search className="mb-3 h-12 w-12 text-slate-300" />
            <h3 className="text-base font-semibold text-slate-700">
              Start searching
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Enter a username (and optionally a reference number) to find
              documents.
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileSignature className="mb-3 h-12 w-12 text-slate-300" />
            <h3 className="text-base font-semibold text-slate-700">
              No results found
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Try a different username or reference.
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Found <span className="font-semibold">{totalCount}</span>{" "}
                {totalCount === 1 ? "result" : "results"}
              </p>
            </div>

            {items.map((item) => {
              const expiryRaw =
                item.expire_at || item.expiry_date || item.expiry;
              const expiryDate = expiryRaw ? new Date(expiryRaw) : null;
              const isExpired =
                expiryDate && !isNaN(expiryDate) && expiryDate < new Date();
              const rawStatus = item.status?.toUpperCase();
              const isCompleted = rawStatus === "COMPLETED";
              const status =
                isExpired && !isCompleted ? "EXPIRED" : rawStatus;
              const docName = item.documents?.[0]?.name || "Document";

              return (
                <button
                  key={item.id}
                  onClick={() => handleOpen(item)}
                  className="group flex w-full flex-col justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition active:scale-[0.995] active:bg-slate-50 md:flex-row md:items-center md:p-4"
                >
                  <div className="flex min-w-0 flex-col gap-2.5">
                    <div
                      className="truncate text-lg font-bold text-slate-900 md:text-base"
                      title={docName}
                    >
                      {docName}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 md:text-xs">
                        {item.customerFirstName} {item.customerLastName}
                      </span>
                      <span className="rounded-full bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 md:text-xs">
                        {item.customerEmail}
                      </span>
                      {item.customerPhone && (
                        <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 md:text-xs">
                          {item.customerPhone}
                        </span>
                      )}
                      {expiryDate && !isNaN(expiryDate) && (
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium md:text-xs ${
                            isExpired
                              ? "bg-red-100 text-red-700"
                              : "bg-rose-50 text-rose-700"
                          }`}
                        >
                          <Calendar className="h-3.5 w-3.5" />
                          {isExpired ? "Expired" : "Expires"}{" "}
                          {expiryDate.toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                          {" · "}
                          {expiryDate.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col items-start gap-2 md:items-end">
                    <div className="flex flex-wrap items-center gap-2 md:justify-end">
                      <span className="rounded-full bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white md:text-xs">
                        #{item.referenceNumber}
                      </span>
                      {item.referenceType && (
                        <span className="rounded-full bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-700 md:text-xs">
                          {item.referenceType}
                        </span>
                      )}
                      {item.documentType && (
                        <span className="rounded-full bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-700 md:text-xs">
                          {item.documentType}
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold md:text-xs ${
                          isCompleted
                            ? "bg-green-100 text-green-700"
                            : isExpired
                            ? "bg-red-100 text-red-700"
                            : "bg-sky-100 text-sky-700"
                        }`}
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${
                            isCompleted
                              ? "bg-green-600"
                              : isExpired
                              ? "bg-red-600"
                              : "bg-sky-600"
                          }`}
                        />
                        {status}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 rounded-md bg-sky-50 px-2.5 py-1.5 text-sm text-sky-700 md:text-xs">
                      <Calendar className="h-4 w-4 md:h-3.5 md:w-3.5" />
                      {new Date(item.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </button>
              );
            })}

            {totalPages > 1 && (
              <div className="mt-6 flex flex-col items-center justify-between gap-3 md:flex-row">
                <p className="text-sm text-slate-600">
                  Showing {(page - 1) * LIMIT + 1}–
                  {Math.min(page * LIMIT, totalCount)} of {totalCount}
                </p>

                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => runSearch(page - 1)}
                    className="min-w-[64px] rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 active:bg-slate-100 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const p = i + 1;
                    return (
                      <button
                        key={p}
                        onClick={() => runSearch(p)}
                        className={`min-w-[44px] rounded-xl px-4 py-2.5 text-sm font-semibold ${
                          page === p
                            ? "bg-sky-500 text-white"
                            : "bg-slate-100 text-slate-700 active:bg-slate-200"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    disabled={page === totalPages}
                    onClick={() => runSearch(page + 1)}
                    className="min-w-[64px] rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 active:bg-slate-100 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>

    </div>
  );
}
